import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Palette, Trash2, Download, FolderKanban, Save, FileUp, FileText } from "lucide-react";
import api, { API } from "@/lib/api";

const EMPTY_BRAND = { brand_name: "", tagline: "", colors: [], fonts: [], tone: "", audience: "", rules: "" };

export function WorkspacePanel({ workspaces, wsId, onSelect, onChange }) {
  const [name, setName] = useState("");
  const [objective, setObjective] = useState("");
  const [brand, setBrand] = useState(null);
  const [docs, setDocs] = useState([]);
  const fileRef = useRef(null);
  const ws = workspaces.find((w) => w.workspace_id === wsId);

  const loadDocs = (id) => (id ? api.get(`/workspaces/${id}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([])) : setDocs([]));
  useEffect(() => { loadDocs(wsId); }, [wsId]);
  const uploadDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post(`/workspaces/${wsId}/documents`, fd); toast.success(`Ingested ${r.data.name} · ${r.data.chunks} chunks`); loadDocs(wsId); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not ingest document"); }
    e.target.value = "";
  };
  const removeDoc = async (id) => { await api.delete(`/workspaces/${wsId}/documents/${id}`); loadDocs(wsId); };

  const create = async () => {
    if (!name.trim()) return toast.error("Give the workspace a name");
    const r = await api.post("/workspaces", { name, objective });
    onChange([r.data, ...workspaces]); onSelect(r.data.workspace_id); setName(""); setObjective("");
    toast.success("Workspace created");
  };
  const remove = async () => {
    await api.delete(`/workspaces/${wsId}`);
    const rest = workspaces.filter((w) => w.workspace_id !== wsId);
    onChange(rest); onSelect(rest[0]?.workspace_id || ""); toast.message("Workspace deleted");
  };
  const saveBrand = async () => {
    const payload = { ...brand, colors: (brand.colors_s ?? brand.colors.join(", ")).split(",").map((s) => s.trim()).filter(Boolean), fonts: (brand.fonts_s ?? brand.fonts.join(", ")).split(",").map((s) => s.trim()).filter(Boolean) };
    delete payload.colors_s; delete payload.fonts_s;
    const r = await api.put(`/workspaces/${wsId}/brand`, payload);
    onChange(workspaces.map((w) => (w.workspace_id === wsId ? { ...w, brand: r.data.brand } : w)));
    setBrand(null); toast.success("Brand kit saved — the Director will follow it");
  };

  return (
    <aside className="space-y-4" data-testid="workspace-panel">
      <div className="glass rounded-2xl p-4">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2"><FolderKanban size={13} /> Workspaces</div>
        <div className="space-y-1 max-h-56 overflow-y-auto">
          <button data-testid="workspace-adhoc" onClick={() => onSelect("")} className={`w-full text-left px-3 py-2 rounded-lg text-sm ${!wsId ? "bg-purple-600/20 text-white" : "text-slate-400 hover:bg-white/5"}`}>Ad-hoc (no workspace)</button>
          {workspaces.map((w) => (
            <button key={w.workspace_id} data-testid="workspace-item" onClick={() => onSelect(w.workspace_id)} className={`w-full text-left px-3 py-2 rounded-lg text-sm truncate ${w.workspace_id === wsId ? "bg-purple-600/20 text-white" : "text-slate-400 hover:bg-white/5"}`}>
              {w.name} {w.brand?.brand_name && <span className="text-[10px] text-emerald-400 ml-1">● brand</span>}
            </button>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
          <input data-testid="workspace-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New workspace name" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none" />
          <input data-testid="workspace-objective-input" value={objective} onChange={(e) => setObjective(e.target.value)} placeholder="Campaign objective (optional)" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none" />
          <button data-testid="workspace-create-btn" onClick={create} className="w-full inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15"><Plus size={13} /> Create workspace</button>
        </div>
      </div>

      {ws && (
        <div className="glass rounded-2xl p-4 space-y-3" data-testid="workspace-detail">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-sm truncate">{ws.name}</div>
            <div className="flex gap-1">
              <a data-testid="workspace-export-btn" href={`${API}/workspaces/${ws.workspace_id}/export.zip`} className="p-1.5 rounded-md hover:bg-white/10 text-slate-400" title="Export ZIP"><Download size={14} /></a>
              <button data-testid="workspace-delete-btn" onClick={remove} className="p-1.5 rounded-md hover:bg-red-500/10 text-slate-400 hover:text-red-300" title="Delete"><Trash2 size={14} /></button>
            </div>
          </div>
          {ws.objective && <p className="text-xs text-slate-400">{ws.objective}</p>}
          {Object.keys(ws.memory || {}).length > 0 && (
            <div className="text-[11px] text-slate-500 space-y-0.5" data-testid="workspace-memory">
              <div className="font-mono uppercase tracking-widest text-[10px]">Memory</div>
              {Object.entries(ws.memory).map(([k, v]) => <div key={k}><span className="text-slate-300">{k}:</span> {String(v).slice(0, 80)}</div>)}
            </div>
          )}
          {!brand ? (
            <button data-testid="brand-edit-btn" onClick={() => setBrand({ ...EMPTY_BRAND, ...(ws.brand || {}) })} className="w-full inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 hover:border-purple-500/40">
              <Palette size={13} /> {ws.brand?.brand_name ? `Brand kit: ${ws.brand.brand_name}` : "Set up brand kit"}
            </button>
          ) : (
            <div className="space-y-2" data-testid="brand-form">
              {[["brand_name", "Brand name"], ["tagline", "Tagline"], ["tone", "Tone of voice"], ["audience", "Audience"], ["rules", "Rules (never / always)"]].map(([k, l]) => (
                <input key={k} data-testid={`brand-${k}`} value={brand[k] || ""} onChange={(e) => setBrand({ ...brand, [k]: e.target.value })} placeholder={l} className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none" />
              ))}
              <input data-testid="brand-colors" value={brand.colors_s ?? brand.colors.join(", ")} onChange={(e) => setBrand({ ...brand, colors_s: e.target.value })} placeholder="Colours: #1F3D2B, #C8A25A" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none" />
              <input data-testid="brand-fonts" value={brand.fonts_s ?? brand.fonts.join(", ")} onChange={(e) => setBrand({ ...brand, fonts_s: e.target.value })} placeholder="Fonts: Fraunces, Manrope" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none" />
              <div className="flex gap-2">
                <button data-testid="brand-save-btn" onClick={saveBrand} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-purple-600 text-white"><Save size={12} /> Save</button>
                <button onClick={() => setBrand(null)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10">Cancel</button>
              </div>
            </div>
          )}
          {ws.brand?.colors?.length > 0 && <div className="flex gap-1">{ws.brand.colors.map((c) => <span key={c} className="w-5 h-5 rounded-full border border-white/20" style={{ background: c }} title={c} />)}</div>}
          <div className="pt-3 border-t border-white/10 space-y-1.5" data-testid="workspace-documents">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Reference documents · {docs.length}</div>
              <button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/10 hover:border-purple-500/40"><FileUp size={11} /> Add .txt / .md / .pdf</button>
              <input ref={fileRef} data-testid="doc-file-input" type="file" accept=".txt,.md,.pdf,text/plain,application/pdf" className="hidden" onChange={uploadDoc} />
            </div>
            {docs.map((d) => (
              <div key={d.doc_id} data-testid="doc-item" className="flex items-center gap-2 text-xs text-slate-300">
                <FileText size={12} className="text-slate-500 shrink-0" /><span className="truncate flex-1" title={d.name}>{d.name}</span><span className="text-[10px] text-slate-500 font-mono">{d.chars} ch</span>
                <button data-testid="doc-delete-btn" onClick={() => removeDoc(d.doc_id)} className="text-slate-500 hover:text-red-300"><Trash2 size={11} /></button>
              </div>
            ))}
            {docs.length === 0 && <div className="text-[11px] text-slate-500">The Director searches these (search_documents) before writing.</div>}
          </div>
        </div>
      )}
    </aside>
  );
}
